import React, { useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useHistory } from 'react-router-dom';
import { IconButton, Popover } from '@material-ui/core';
import { MoreVert } from '@material-ui/icons';
import { camelCase } from 'lodash';
import axios from 'axios';
import { Suspend } from '../../components/dialogs/Suspend';
import { Reject } from '../../components/dialogs/Reject';
import { Error } from '../../components/dialogs/Error';
import { StyledPaper } from '../../components/Popper/Popper.styles';
import { mapKeysDeepLodash } from '../../Campaign/utils/utils';
import { useAuth } from '../../context/auth';
import { CampaignStatus } from '../../components/CampaignStatus';
import { Status as Statuses } from '../../statuses';
import { sliceEmail, createDate, changeCampaignType } from '../../utilities';
import { CampaignProps } from './Campaign.interface';
import {
    Row,
    Title,
    MobileWrapper,
    GridWrapper,
    Label,
    Info,
    Wrapper,
    StyledLink
} from './Campaign.styles';

export function Campaign({ adminAccess, data, refresh }: CampaignProps) {
    const { id, title, status, startDate, endDate, adType } = data;
    const history = useHistory();
    const { authToken } = useAuth();
    const [showSuspend, setShowSuspend] = useState(false);
    const [showReject, setShowReject] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const showMoreStatuses = ['active', 'suspended', 'waiting']; 
    const open = Boolean(anchorEl);
    const idPopper = open ? 'simple-popper' : undefined;

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    }
    const makeActive = () => {
        axios.patch(`${process.env.REACT_APP_BASE_URL}/api/campaign/campaign/${id}/start/`, {}, {headers: { DTOKEN: `${authToken}`}}).then(() => {
            refresh();
        })
    }
    const approveCampaign = () => {
        axios.patch(`${process.env.REACT_APP_BASE_URL}/api/campaign/campaign/${id}/approve/`, {}, {headers: { DTOKEN: `${authToken}`}})
        .then(() => {
            refresh();
        })
        .catch(error => {
            setErrorMessage(error.response.data.message);
            setShowError(true);
        })
    }
    const duplicateCampaign = () => {
        axios.get(`${process.env.REACT_APP_BASE_URL}/api/campaign/campaign/${id}/edit/`, { headers: { DTOKEN: `${authToken}` } }).then(res => {
            const mappedValues = mapKeysDeepLodash(res.data, (v: any, k: any) => camelCase(k));
            sessionStorage.setItem('disabledTypeChange', 'true');
            history.push(`${process.env.REACT_APP_INTERNAL_URL}create-campaign`, { initialDuplicateValues: {...mappedValues, basicInformation: {...mappedValues.basicInformation, title: `${mappedValues.basicInformation.title} copy`}, content: {...mappedValues.content, campaignType: mappedValues.basicInformation.adType}} })
        })
    };
    return (
        <>
            {isMobile
                ?
                    <MobileWrapper>
                        <Wrapper>
                            <Title>{title}</Title>
                            {adminAccess && showMoreStatuses.includes(status) &&
                                <IconButton onClick={handleClick}>
                                    <MoreVert />
                                </IconButton>
                            }
                        </Wrapper>
                        <StyledLink to={`${process.env.REACT_APP_INTERNAL_URL}campaign-details/${id}`}>
                            <Label>Status:</Label>
                            <CampaignStatus status={status} />
                            {adminAccess &&
                                <>
                                    <Label>Owner:</Label>
                                    <Info><b>{data.owner}</b></Info>
                                </>
                            }
                            <GridWrapper>
                                <div>
                                    <Label>Start Date:</Label>
                                    <Info>{createDate(startDate)}</Info>
                                </div>
                                <div>
                                    <Label>End Date:</Label>
                                    <Info>{endDate ? createDate(endDate) : '-'}</Info>
                                </div>
                                <div>
                                    <Label>Type:</Label>
                                    <Info>{changeCampaignType(adType)}</Info>
                                </div>
                                <div>
                                    <Label>DovePoints Used:</Label>
                                    <Info>{data.dovepointsLimit ? `${data.dovepointsUsed}/${data.dovepointsLimit}`: data.dovepointsUsed}</Info>
                                </div>
                            </GridWrapper>
                        </StyledLink>
                    </MobileWrapper>
                :
                    <Row adminAccess={adminAccess}>
                        <StyledLink to={`${process.env.REACT_APP_INTERNAL_URL}campaign-details/${id}`}>
                            <Title>{title}</Title>
                            {adminAccess && <p>{sliceEmail(data.owner, 18)}</p>}
                            <CampaignStatus status={status} />
                            <p>{changeCampaignType(adType)}</p>
                            <p>{createDate(startDate)}</p>
                            <p>{data.dovepointsLimit ? `${data.dovepointsUsed}/${data.dovepointsLimit}`: data.dovepointsUsed}</p>
                        </StyledLink>
                        {adminAccess && showMoreStatuses.includes(status) &&
                            <IconButton onClick={handleClick}>
                                <MoreVert />
                            </IconButton>
                        }
                    </Row>
            }
            <Popover 
                id={idPopper} 
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <StyledPaper>
                    {status === Statuses.Active && 
                        <button onClick={() => {
                            handleClose();
                            setShowSuspend(true);
                        }}>
                            Suspend
                        </button>
                    }
                    {status === Statuses.Suspended && 
                        <button onClick={() => {
                            makeActive();
                            handleClose();
                        }}>
                            Make Active
                        </button>
                    }
                    {status === Statuses.WaitingForApproval && 
                        <>
                            <button onClick={() => {
                                approveCampaign();
                                handleClose();
                            }}>
                                Approve
                            </button>
                            <button onClick={() => {
                                handleClose();
                                setShowReject(true);
                            }}>
                                Reject
                            </button>
                        </>
                    }
                    <button onClick={() => {
                        duplicateCampaign();
                        handleClose();
                    }}>
                        Duplicate
                    </button>
                </StyledPaper>
            </Popover>
            <Suspend open={showSuspend} setOpen={setShowSuspend} campaignId={id} refresh={refresh}/>
            <Reject open={showReject} setOpen={setShowReject} campaignId={id} refresh={refresh} />
            <Error open={showError} setOpen={setShowError} message={errorMessage} />
        </>
    )
}
